import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
  OnDestroy,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ThemeService } from '../../../../core/core';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import {
  SKUItem,
  DropdownOption,
  TreeNode,
  ProductType,
  StatusUtils,
  ProductStatus,
  SpuTypeUtils,
} from '../../models/product.interface';
import { ProductService } from '../../services/product.service';
import {
  SearchFormComponent,
  FormFieldConfig,
  FILTER_ACTIVE_PARAM,
} from '@shared/components/search-form/search-form.component';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import { LastCategoryPipe } from '@shared/pipes/last-category.pipe';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { Subject, takeUntil, switchMap, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-product-review',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    TagModule,
    CheckboxModule,
    TooltipModule,
    SearchFormComponent,
    DataTableComponent,
    LastCategoryPipe,
    AppButtonComponent,
  ],
  templateUrl: './product-review.component.html',
  styleUrl: './product-review.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductReviewComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly productService = inject(ProductService);
  private readonly translateService = inject(TranslateService);
  protected readonly themeService = inject(ThemeService);
  private readonly destroy$ = new Subject<void>();

  activeTab = signal<ProductType>('ordinary');
  currentPage = signal(0);
  itemsPerPage = signal(20);
  totalItems = signal(0);
  loading = signal(false);

  categoryOptions = this.productService.categoryOptions;
  statusOptions = computed(() => {
    // Map status options to use i18n keys for labels while keeping English labels as values for filtering
    const baseOptions = this.productService.statusOptions;
    return baseOptions.map((option) => {
      let i18nKey = '';
      // Map English labels to i18n keys for display
      if (option.label === 'Pending Approval') {
        i18nKey = 'goods.product_review.status.pending_approval';
      } else if (option.label === 'Completed') {
        i18nKey = 'goods.product_review.status.completed';
      } else if (option.label === 'Rejected') {
        i18nKey = 'goods.product_review.status.rejected';
      }
      // Keep original English label as value for filtering compatibility
      return {
        label: i18nKey || option.label,
        value: option.label, // Use original English label as value for StatusUtils.getStatusValuesByLabel()
      };
    });
  });
  classificationOptions = signal<TreeNode[]>([]);

  skuItems = signal<SKUItem[]>([]);
  filteredItems = signal<SKUItem[]>([]);

  // Search form configuration with enhanced form controls
  searchFields = computed<FormFieldConfig[]>(() => [
    {
      key: 'skuName',
      label: 'goods.product_review.search.fields.sku_name',
      type: 'text',
      placeholder: 'goods.product_review.search.placeholders.enter_sku_name',
    },
    {
      key: 'productName',
      label: 'goods.product_review.search.fields.product_name',
      type: 'text',
      placeholder:
        'goods.product_review.search.placeholders.enter_product_name',
    },
    {
      key: 'category',
      label: 'goods.product_review.search.fields.product_type',
      type: 'select',
      options: this.categoryOptions,
      placeholder:
        'goods.product_review.search.placeholders.select_product_type',
    },
    {
      key: 'classification',
      label: 'goods.product_review.search.fields.product_category',
      type: 'treeselect',
      treeOptions: this.classificationOptions(),
      placeholder:
        'goods.product_review.search.placeholders.select_product_category',
    },
    {
      key: 'status',
      label: 'goods.product_review.search.fields.status',
      type: 'multiselect',
      options: this.statusOptions(),
      placeholder: 'goods.product_review.search.placeholders.select_status',
    },
  ]);

  // Table columns configuration
  tableColumns: TableColumn[] = [
    {
      prop: 'categoryName',
      label: 'goods.product_review.table.columns.product_category',
      width: 150,
      type: 'custom',
      template: 'categoryTemplate',
    },
    {
      prop: 'spuCode',
      label: 'goods.product_review.table.columns.product_code',
      width: 120,
    },
    {
      prop: 'spuName',
      label: 'goods.product_review.table.columns.product_name',
      width: 160,
    },
    {
      prop: 'skuCode',
      label: 'goods.product_review.table.columns.sku_code',
      width: 120,
    },
    {
      prop: 'skuName',
      label: 'goods.product_review.table.columns.sku_name',
      width: 120,
    },
    {
      prop: 'spuType',
      label: 'goods.product_review.table.columns.product_type',
      width: 100,
      type: 'text',
    },
    {
      prop: 'materialName',
      label: 'goods.product_review.table.columns.asset_name',
      width: 120,
    },
    {
      prop: 'materialMainImage',
      label: 'goods.product_review.table.columns.asset_image',
      width: 80,
      type: 'custom',
    },
    {
      prop: 'goodsRemark',
      label: 'goods.product_review.table.columns.remarks',
      width: 140,
    },
    {
      prop: 'status',
      label: 'goods.product_review.table.columns.status',
      width: 100,
      type: 'status',
    },
    {
      prop: 'auditCommitDate',
      label: 'goods.product_review.table.columns.approval_time',
      width: 120,
      type: 'custom',
    },
    {
      prop: 'actions',
      label: 'goods.product_review.table.columns.actions',
      width: 120,
      type: 'actions',
    },
  ];

  ngOnInit(): void {
    this.setupRouteListener();
    this.loadCategoryTree();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCategoryTree(): void {
    this.productService.getCategoryTree().subscribe({
      next: (categories) => {
        this.classificationOptions.set(categories);
      },
      error: (error) => {
        console.error('Error loading category tree:', error);
      },
    });
    this.checkInitialLoad();
  }

  private loadSkuItems(searchData: any = {}): void {
    this.loading.set(true);
    const setFlag = this.getCurrentTabSetFlag();
    const requestData = { ...searchData, setFlag };

    this.productService
      .getSkuItems(this.currentPage(), this.itemsPerPage(), requestData)
      .subscribe({
        next: (response) => {
          this.skuItems.set(response.items);
          this.totalItems.set(response.total);
          this.currentPage.set(response.page);
          this.updateFilteredItems();
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading SKU items:', error);
          this.loading.set(false);
        },
      });
  }

  private checkInitialLoad(): void {
    const urlParams = this.route.snapshot.queryParams;
    const hasSearchParams = urlParams[FILTER_ACTIVE_PARAM] === 'true';

    if (!hasSearchParams) {
      this.loadSkuItems();
    }
  }

  private setupRouteListener(): void {
    this.route.params
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => prev['type'] === curr['type']),
      )
      .subscribe((params) => {
        const type = params['type'] as ProductType;
        if (type === 'package' || type === 'ordinary') {
          this.activeTab.set(type);
        } else {
          this.activeTab.set('ordinary');
        }

        // Load data only on initial route change, not on subsequent changes
        if (!this.skuItems().length) {
          this.loadSkuItems();
        }
      });
  }

  onTabChange(tab: ProductType): void {
    this.activeTab.set(tab);
    this.router.navigate(['/goods/audit/sku', tab]);
    // Explicitly load data for the new tab
    this.loadSkuItems();
  }

  onSearch(searchData: any): void {
    console.log('searchData', searchData);
    if (searchData.classification) {
      searchData.categoryIdList = this.flatClassification(
        searchData.classification,
      );
    } else {
      searchData.categoryIdList = [];
    }

    this.currentPage.set(0); // Reset to first page when searching
    this.loadSkuItems(searchData);
  }

  onReset(): void {
    this.currentPage.set(0); // Reset to first page when resetting
    this.loadSkuItems();
  }

  onCheck(item: SKUItem): void {
    console.log('Check SKU:', item);
    const type = this.activeTab();
    this.router.navigate(['/goods/audit', type, 'view', item.id]);
  }

  onAudit(item: SKUItem): void {
    console.log('Audit SKU:', item);
    const type = this.activeTab();
    this.router.navigate(['/goods/audit', type, 'audit', item.id]);
  }

  onPageChange(event: any): void {
    // Handle both page changes and row size changes
    this.currentPage.set(event.page);
    this.itemsPerPage.set(event.rows);
    this.loadSkuItems();
  }

  // Updated status methods using StatusUtils
  getStatusSeverity(
    status: ProductStatus,
  ): 'warning' | 'success' | 'danger' | 'info' {
    return StatusUtils.getStatusSeverity(status);
  }

  getStatusDisplayText(status: ProductStatus): string {
    const config = StatusUtils.getStatusConfig(status);
    if (!config) return `Status ${status}`;

    // Map status to i18n keys based on the standard retail terms
    let i18nKey = '';
    if (status === 2 || status === 4) {
      i18nKey = 'goods.product_review.status.under_review';
    } else if (status === 3) {
      i18nKey = 'goods.product_review.status.completed';
    } else if (status === 1 || status === 5) {
      i18nKey = 'goods.product_review.status.rejected';
    }

    return i18nKey ? this.translateService.instant(i18nKey) : config.label;
  }

  getStatusIcon(status: ProductStatus): string {
    return StatusUtils.getStatusIcon(status);
  }

  getStatusStyle(status: ProductStatus): any {
    const severity = StatusUtils.getStatusSeverity(status);
    switch (severity) {
      case 'warning':
        return {
          'background-color': '#fef3c7',
          color: '#a16207',
          border: 'none',
        };
      case 'success':
        return {
          'background-color': '#ecfdf5',
          color: '#047857',
          border: 'none',
        };
      case 'danger':
        return {
          'background-color': '#fef2f2',
          color: '#dc2626',
          border: 'none',
        };
      default:
        return {
          'background-color': '#f3f4f6',
          color: '#6b7280',
          border: 'none',
        };
    }
  }

  private getCurrentTabSetFlag(): number {
    return this.activeTab() === 'package' ? 1 : 0;
  }

  private updateFilteredItems(): void {
    // Since we're now using backend pagination, we don't need client-side filtering
    // The filtered items are the same as the loaded items
    this.filteredItems.set(this.skuItems());
  }

  private flatClassification(classification: any[]): any[] {
    const result = new Set<number>();

    const flatten = (items: any[]): void => {
      items.forEach((item) => {
        // Add current item's value if it exists
        if (item.value !== undefined) {
          result.add(item.value);
        }

        // Recursively process children if they exist
        if (item.children && item.children.length > 0) {
          flatten(item.children);
        }
      });
    };

    flatten(classification);
    return Array.from(result);
  }

  /**
   * Format date string for display
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return '-';
    }
  }
}
